import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { logError } from 'utils/logger';
import { EMPTY_PROJECTS_BY_STATUS } from '../constants';
import { getAnalystProjects } from '../service';

/**
 * Fetches and manages analyst projects for the logged-in analyst.
 *
 * @returns {{
 *   projectsByStatus: {
 *     not_started: Array,
 *     assigned: Array,
 *     in_progress: Array,
 *     on_hold: Array,
 *     completed: Array,
 *     cancelled: Array
 *   },
 *   financialYear: string | null,
 *   loading: boolean,
 *   refresh: Function
 * }}
 */
export function useAnalystProjectList() {
  const [projectsByStatus, setProjectsByStatus] = useState(EMPTY_PROJECTS_BY_STATUS);
  const [financialYear, setFinancialYear] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAnalystProjects();
      setProjectsByStatus(data.projectsByStatus);
      setFinancialYear(data.financialYear);
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to fetch analyst projects',
          error: err,
          code: 'ANALYST_PROJECTS_FETCH_FAILED',
        }),
      );
      setProjectsByStatus(EMPTY_PROJECTS_BY_STATUS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projectsByStatus, financialYear, loading };
}
